import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { useMe } from '@/auth/queries';
import { recaptchaHeader, recaptchaToken } from '@/auth/recaptcha';
import { tratamiento } from '@/lib/utils';

const AREAS = [
  { value: 'navegabilidad', label: 'Navegabilidad en la plataforma' },
  { value: 'buscador', label: 'Buscador' },
  { value: 'contenido', label: 'Contenido' },
  { value: 'cursopresencial', label: 'Curso presencial' },
  { value: 'otros', label: 'Otros' },
] as const;
type Area = (typeof AREAS)[number]['value'];

/** "¡Tu opinión nos interesa!" (port de `inicio/opinion`), con reCAPTCHA v3 validado en la API. */
export function OpinionPage() {
  const { data: me } = useMe();
  const [area, setArea] = useState<Area | ''>('');
  const [opinion, setOpinion] = useState('');
  const enviar = useMutation({
    mutationFn: async () => {
      const token = await recaptchaToken('feedback');
      return unwrap(
        api.POST('/api/v1/me/feedback', {
          body: { area: area as Area, opinion },
          headers: recaptchaHeader(token),
        }),
      );
    },
    onSuccess: () => {
      toast.success('¡Gracias! Recibimos tu opinión y la revisará el equipo de soporte.');
      setArea('');
      setOpinion('');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">¡Tu opinión nos interesa!</span>
      </h1>
      {/* Tarjeta con el cerebro asomado arriba al centro (`icon-top-middle` del legacy). */}
      <form
        className="card-cerebro mt-12 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          enviar.mutate();
        }}
      >
        <img
          src="/img/isotipo-cerebro-azul.png"
          alt=""
          className="card-cerebro__isotipo solo-claro"
        />
        <img
          src="/img/isotipo-cerebro-blanco.png"
          alt=""
          className="card-cerebro__isotipo solo-oscuro"
        />
        <div className="space-y-1">
          <p className="font-extrabold text-texto">
            Hola {me ? tratamiento(me.nickname || me.name, me.gender) : ''},
          </p>
          <p className="text-texto-2">
            Queremos seguir mejorando. ¡Tu opinión es muy importante para nosotros!
          </p>
          <p className="text-texto-2">
            ¿Qué consideras que podría mejorar tu aprendizaje dentro de la plataforma Consalud
            ENARM?
          </p>
        </div>
        <label className="block text-sm font-bold">
          Selecciona un área de mejora
          <select
            className="input-vidrio mt-1 w-full font-normal"
            required
            value={area}
            onChange={(e) => setArea(e.target.value as Area)}
          >
            <option value="">Elige un área</option>
            {AREAS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-bold">
          Describe ¿cómo podríamos mejorar?
          <textarea
            className="input-vidrio mt-1 min-h-36 w-full font-normal"
            required
            minLength={5}
            maxLength={500}
            value={opinion}
            onChange={(e) => setOpinion(e.target.value)}
          />
          <span className="mt-1 block text-right text-xs font-normal text-texto-3 tabular">
            {opinion.length}/500
          </span>
        </label>
        <div className="text-right">
          <button
            type="submit"
            className="btn-cs btn-exito"
            disabled={!area || opinion.trim().length < 5 || enviar.isPending}
          >
            {enviar.isPending ? 'Enviando…' : 'Enviar mi opinión'}
          </button>
        </div>
      </form>
    </div>
  );
}
